// Controls for non-destructive trim / fade / normalize / reverse
// preview. The values live on the parent; this is pure presentation.
//
// Kept minimal: sliders for fade in/out, toggles for normalize + reverse.
// Resample-rate picker deferred to the commit bar (B2) since it changes
// buffer duration, which changes marker math and is conceptually a
// post-trim step.

import { memo, useCallback } from 'react';

export type TrimState = {
  fadeInMs: number;
  fadeOutMs: number;
  normalize: boolean;
  reverse: boolean;
};

export const DEFAULT_TRIM_STATE: TrimState = {
  fadeInMs: 0,
  fadeOutMs: 0,
  normalize: false,
  reverse: false,
};

type Props = {
  state: TrimState;
  onChange: (next: TrimState) => void;
  maxFadeMs?: number;
};

function TrimControlsInner({ state, onChange, maxFadeMs = 500 }: Props) {
  const patch = useCallback(
    (diff: Partial<TrimState>) => onChange({ ...state, ...diff }),
    [state, onChange],
  );

  return (
    <div className="flex flex-wrap gap-4 items-center px-3 py-2 bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs">
      <label className="flex items-center gap-2" title="Fade-in over the first N ms">
        <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500">Fade in</span>
        <input
          type="range"
          min={0}
          max={maxFadeMs}
          step={1}
          value={state.fadeInMs}
          onChange={(e) => patch({ fadeInMs: Number(e.target.value) })}
          className="accent-yellow-300 w-32"
        />
        <span className="tabular-nums w-10 text-right">{state.fadeInMs}ms</span>
      </label>

      <label className="flex items-center gap-2" title="Fade-out over the last N ms">
        <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500">Fade out</span>
        <input
          type="range"
          min={0}
          max={maxFadeMs}
          step={1}
          value={state.fadeOutMs}
          onChange={(e) => patch({ fadeOutMs: Number(e.target.value) })}
          className="accent-yellow-300 w-32"
        />
        <span className="tabular-nums w-10 text-right">{state.fadeOutMs}ms</span>
      </label>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={state.normalize}
          onChange={(e) => patch({ normalize: e.target.checked })}
          className="accent-yellow-300"
        />
        <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500">Normalize</span>
      </label>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={state.reverse}
          onChange={(e) => patch({ reverse: e.target.checked })}
          className="accent-yellow-300"
        />
        <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500">Reverse</span>
      </label>
    </div>
  );
}

export const TrimControls = memo(TrimControlsInner);
